import React, {
  useEffect,
  useRef,
  useState,
} from 'react'
import get from 'lodash/get'
import isString from 'lodash/isString'
import parseInt from 'lodash/parseInt'
import compose from 'recompose/compose'
import { copyToClipboard } from '../../services/share/index'
import { getPasswordStrength } from '../../services/password/index'
import { connect as connectStaticText } from '../StaticText/connect'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import {
  TYPES, getTag, getType, getClass, getLabelId, getInputId,
} from '../FormInput.v2/utils'
import { useLegacyFormField } from './useLegacyFormField'
import type {
  FormFieldStaticTextProps,
  FormInputAppearanceProps,
  FormTextFieldLabel,
  LegacyValidationProps,
} from './formFieldTypes'

const STRENGTH_INDICATOR_CLASS_NAME = 'forminput--password__strength-indicator'

function getStrengthIndicatorClassName (value: string) {
  const strength = getPasswordStrength(value)
  return `${STRENGTH_INDICATOR_CLASS_NAME} ${STRENGTH_INDICATOR_CLASS_NAME}--${strength}`
}

export type TextInputProps =
  FormFieldStaticTextProps
  & FormInputAppearanceProps
  & LegacyValidationProps & {
  name: string
  label: FormTextFieldLabel
  type?: string
  validationTimeout?: number
  errorOnPristine?: boolean
  autocomplete?: string
  placeholder?: string
  showCharCount?: boolean
  forceError?: string
  maxLength?: number
  onChange?: (
    value: string,
    props: TextInputProps,
    event: React.ChangeEvent<HTMLElement>,
  ) => string | undefined
  copyable?: boolean
  restrict?: boolean
  onClick?: () => void
  onBlur?: (value: string, props: TextInputProps) => string | undefined
  value?: string
  autogrow?: boolean
  showStrength?: boolean
  tabIndex?: string
  autofocus?: boolean
  showCheckMark?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>
}

function getResolvedMaxLength (props: Pick<TextInputProps, 'maxLength' | 'validations'>) {
  if (props.maxLength !== undefined) {
    return props.maxLength
  }
  const { validations } = props
  if (isString(validations)) {
    const match = /maxLength:\s?(\d+)/.exec(validations)
    return match ? parseInt(match[1], 10) : undefined
  }
  const fromObject = get(validations, 'maxLength')
  return typeof fromObject === 'number' ? fromObject : undefined
}

/**
 * Text field wired to Form context. Styling matches FormInput.v2.
 * Prefer validating via `Form` `schema`; `validations` / `validationErrors` exist only
 * to ease Formsy ports and should be removed from the call site when that port is done.
 */
function TextInput (props: TextInputProps) {
  const {
    staticText,
    name,
    type = TYPES.TEXT,
    label,
    required,
    validations,
    validationErrors,
    validationError,
    validationErrorFormat,
    validationTimeout = 1,
    errorOnPristine = false,
    forceError,
    value: valueProp,
    onChange: onChangeProp,
    onBlur: onBlurProp,
    showCharCount,
    showCheckMark,
    copyable,
    onClick,
    autofocus,
    tabIndex,
    note,
    showStrength,
    validate,
  } = props

  const {
    field,
    displayValue,
    externallyControlled,
    setValue,
    getDisplayedError,
    afterValueChange,
    afterBlur,
  } = useLegacyFormField<string>({
    name,
    defaultValue: '',
    staticText,
    required,
    validations,
    validationErrors,
    validationError,
    validationErrorFormat,
    forceError,
    errorOnPristine,
    validationTimeout,
    value: valueProp,
    onChange: onChangeProp as ((...args: unknown[]) => unknown) | undefined,
    validate,
  })

  const [copied, setCopied] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const inputRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const el = inputRef.current as HTMLInputElement | HTMLTextAreaElement | null
    if (autofocus && el) {
      el.focus()
    }
  }, [autofocus])

  const onChange = (event: React.ChangeEvent<HTMLElement>) => {
    const { value } = event.currentTarget as HTMLInputElement
    let nextValue = value

    if (onChangeProp) {
      const valueOnChange = onChangeProp(nextValue, props, event)
      if (valueOnChange !== undefined) {
        nextValue = valueOnChange
      }
    }

    field.onChange(nextValue)
    if (externallyControlled) {
      setValue(name, nextValue, { shouldValidate: true })
    }

    afterValueChange(nextValue)
  }

  const onBlur = (event: React.FocusEvent<HTMLElement>) => {
    const { value } = event.currentTarget as HTMLInputElement
    let modifiedValue = value

    if (onBlurProp) {
      const blurResult = onBlurProp(modifiedValue, props)
      if (blurResult) {
        modifiedValue = blurResult
        field.onChange(modifiedValue)
        setValue(name, modifiedValue, { shouldValidate: true })
      }
    }

    field.onBlur()
    setIsActive(modifiedValue !== '')

    afterBlur(modifiedValue)
  }

  const onFocus = () => {
    setIsActive(true)
  }

  const onCopy = (event: React.MouseEvent) => {
    event.preventDefault()
    const id = getInputId(name)
    const input = document.getElementById(id)
    setCopied(input ? copyToClipboard(input) : false)
    if (onClick) {
      onClick()
    }
  }

  const error = getDisplayedError()
  const className = getClass(type, props, error)
  const labelId = getLabelId(name)
  const id = getInputId(name)
  const hidden = type === TYPES.HIDDEN
  const tag = getTag(type)

  const resolvedMaxLength = getResolvedMaxLength(props)

  let characterCount: string | null = null
  if (showCharCount && resolvedMaxLength) {
    characterCount = `${String(displayValue).length}/${resolvedMaxLength}`
  }

  const copyClassName = copied
    ? 'forminput__copy forminput__copy--copied'
    : 'forminput__copy'

  return (
    <div className={className}>
      {React.createElement(tag, {
        autoComplete: props.autocomplete,
        placeholder: props.placeholder,
        className: 'forminput__input',
        maxLength: resolvedMaxLength,
        'aria-labelledby': labelId,
        required,
        readOnly: props.readonly,
        disabled: props.disabled,
        value: displayValue,
        onChange,
        onBlur,
        onFocus,
        onKeyDown: props.onKeyDown,
        type: getType(type),
        ref: inputRef,
        name: id,
        tabIndex,
        id,
      })}
      {!hidden ? (
        <label
          className={`forminput__label${isActive || displayValue ? ' forminput__label--active' : ''}`}
          tabIndex={-1}
          htmlFor={id}
          id={labelId}
        >
          {label}
        </label>
      ) : null}
      {copyable ? (
        <Button
          className={copyClassName}
          onClick={onCopy}
          type={BUTTON_TYPES.LINK}
        >
          {(showCheckMark && copied) ? <IconV2 type={ICON_TYPES.CHECK} /> : null}
          {staticText.getIn(['data', copied ? 'copied' : 'copy'])}
        </Button>
      ) : null}
      {showCharCount && characterCount && !error && !note ? (
        <span className="forminput__maxlength">{characterCount}</span>
      ) : null}
      {note && !error ? (
        <span className="forminput__note">{note}</span>
      ) : null}
      {error ? (
        <span
          role="alert"
          aria-live="polite"
          className="forminput__error"
        >
          {error}
        </span>
      ) : null}
      {showStrength ? (
        <div className={`${STRENGTH_INDICATOR_CLASS_NAME}-wrapper`}>
          <span className={getStrengthIndicatorClassName(displayValue || '')} />
        </div>
      ) : null}
    </div>
  )
}

export default compose(
  connectStaticText({ storeKey: 'formInputV2' }),
)(TextInput as React.ComponentType<unknown>) as React.ComponentType<Omit<TextInputProps, 'staticText'>>
